import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { workshopRouteForSituation } from "@/data/arrGapReport";

const CALENDAR_URL = "https://calendar.app.google/JKhHpKytau7yFiaU8";

function validSessionId(value: string) {
  return /^[A-Za-z0-9_-]{8,64}$/.test(value);
}

export default function BookingPage() {
  const [sessionId, setSessionId] = useState("");
  const markWorkshopClicked = trpc.arrGapCheck.markWorkshopClicked.useMutation();

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("sid") ?? "";
    setSessionId(validSessionId(sid) ? sid : "");
    document.title = "Product Hiring Strategy Workshop | Alder Search";
    return () => { document.title = "Alder Search | Retained Product Executive Search for VC and PE"; };
  }, []);

  const booking = trpc.arrGapCheck.bookingDetails.useQuery(
    { sessionId },
    { enabled: validSessionId(sessionId), retry: false },
  );
  const detail = booking.data;
  const workshopCopy = workshopRouteForSituation(detail?.hiringSituation).bookingDescription;
  const calendarHref = useMemo(() => CALENDAR_URL, []);

  const chooseTime = () => {
    if (sessionId) markWorkshopClicked.mutate({ sessionId });
    window.location.assign(calendarHref);
  };

  return (
    <div className="min-h-screen bg-white text-[#173d2d]">
      <header className="border-b border-[#dfe6df] bg-white">
        <div className="mx-auto flex min-h-20 max-w-5xl items-center justify-between px-5 sm:px-8">
          <Link href="/" className="font-display text-xl font-bold text-[#173d2d] no-underline">Alder Search</Link>
          <span className="font-ui text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-[#8f7441]">Product Leadership Research</span>
        </div>
      </header>

      <main className="mx-auto min-h-[calc(100vh-10rem)] max-w-5xl px-5 py-12 sm:px-8 sm:py-16">
        <section className="mx-auto max-w-3xl py-4 sm:py-10">
          <p className="font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#8f7441]">Alder Search</p>
          <h1 className="mt-5 font-display text-4xl leading-[1.1] tracking-[-0.02em] text-[#173d2d] sm:text-5xl">Product Hiring Strategy Workshop</h1>
          <p className="mt-5 font-body text-xl leading-8 text-[#456052]">30 minutes, free, one to one.</p>

          <div className="mt-10 border-t-2 border-[#8f7441] bg-[#f7f9f7] p-6 sm:p-8">
            <h2 className="font-ui text-xs font-semibold uppercase tracking-[0.14em] text-[#8f7441]">What we will do</h2>
            <p className="mt-4 font-body text-lg leading-7 text-[#456052]">{workshopCopy}</p>
          </div>

          {detail && (
            <div className="mt-6 grid gap-4 border border-[#dbe4dc] bg-white p-6 sm:grid-cols-3">
              <div><p className="font-ui text-xs font-semibold uppercase tracking-[0.14em] text-[#8f7441]">Readiness score</p><p className="mt-2 font-display text-3xl text-[#173d2d]">{detail.readiness}/100</p></div>
              <div><p className="font-ui text-xs font-semibold uppercase tracking-[0.14em] text-[#8f7441]">Recommended level</p><p className="mt-2 font-body text-lg leading-7 text-[#173d2d]">{detail.suggestedLevel}</p></div>
              <div><p className="font-ui text-xs font-semibold uppercase tracking-[0.14em] text-[#8f7441]">First thing to fix</p><p className="mt-2 font-body text-lg leading-7 text-[#173d2d]">{detail.firstFix ?? "No major risk found"}</p></div>
            </div>
          )}

          <div className="mt-8 border-t border-[#dfe6df] pt-8">
            <h2 className="font-ui text-xs font-semibold uppercase tracking-[0.14em] text-[#8f7441]">Bring</h2>
            <p className="mt-4 font-body text-lg leading-7 text-[#456052]">Your draft spec if you have one. If you do not, a rough sense of the role is enough.</p>
          </div>

          <button onClick={chooseTime} disabled={markWorkshopClicked.isPending} className="mt-10 min-h-14 bg-[#173d2d] px-8 font-ui text-sm font-semibold text-white transition hover:bg-[#8f7441] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2">Choose a time</button>
          <p className="mt-5 font-body text-base leading-7 text-[#456052]">Prefer not to book? Reply to your report email and I will answer there.</p>
        </section>
      </main>

      <footer className="border-t border-[#dfe6df] bg-[#f7f9f7] py-8"><div className="mx-auto flex max-w-5xl flex-col gap-2 px-5 font-ui text-xs text-[#657a6d] sm:flex-row sm:items-center sm:justify-between sm:px-8"><span>Alder Search | Product Leadership Research</span><Link href="/privacy" className="underline underline-offset-2">Privacy notice</Link></div></footer>
    </div>
  );
}
